import { Component, inject, signal, input, output } from '@angular/core';

import { Router } from '@angular/router';
import {
  NavMobileMenuLinkComponent,
  NavMobileMenuLinkConfig,
} from '@sandbox-platform/shared-ui';

@Component({
  standalone: true,
  imports: [NavMobileMenuLinkComponent],
  selector: 'lib-expert-mobile-menu',
  template: `
    <!-- Mobile backdrop overlay -->
    @if (isOpen()) {
    <div
      class="fixed inset-0 bg-gray-600 bg-opacity-50 z-30 md:hidden transition-opacity duration-300"
      (click)="onClose()"
      [style.animation]="'fadeIn 0.3s ease-out'"
    ></div>
    }

    <!-- Mobile menu -->
    <div class="md:hidden relative z-40">
      <div
        class="overflow-hidden transition-all duration-300 ease-out"
        [style.max-height]="isOpen() ? '500px' : '0px'"
      >
        <div class="bg-gray-800 border-t border-gray-700 shadow-lg">
          <!-- User Profile Section -->
          <div class="px-4 py-3 border-b border-gray-700">
            <div class="flex items-center space-x-3">
              <!-- User Avatar -->
              <div
                class="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md"
              >
                {{ getUserInitials() }}
              </div>
              <!-- User Info -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-white truncate">
                  Expert User
                </p>
                <p class="text-xs text-gray-300 truncate">Expert Console</p>
              </div>
            </div>
          </div>

          <!-- Navigation Items -->
          <div class="pt-2 pb-3 space-y-1">
            @for (item of navigationItems(); track item.routerLink) {
            <lib-nav-mobile-menu-link [config]="item" (clicked)="onClose()" />
            }
          </div>

          <!-- User Actions Section -->
          <div class="border-t border-gray-700 pt-2 pb-3">
            <button
              (click)="goToProfile()"
              class="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-white transition-colors duration-200 focus:outline-none focus:bg-gray-700"
            >
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Profile
              </div>
            </button>
            <button
              (click)="signOut()"
              class="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-red-400 transition-colors duration-200 focus:outline-none focus:bg-gray-700"
            >
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Sign out
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
})
export class MobileMenuComponent {
  private router = inject(Router);

  // Input signals
  isOpen = input.required<boolean>();
  navigationItems = input.required<NavMobileMenuLinkConfig[]>();

  // Output signals
  close = output<void>();

  onClose(): void {
    this.close.emit();
  }

  // Get user initials for avatar
  getUserInitials(): string {
    // For now, return a default. This can be updated when auth store is available
    return 'E';
  }

  goToProfile(): void {
    this.onClose();
    this.router.navigate(['/profile'], {
      state: {
        returnUrl: this.router.url,
      },
    });
  }

  async signOut(): Promise<void> {
    this.onClose();
    // This can be updated when auth store is available
    window.location.href = '/auth/login';
  }
}
